import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Plus } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { img } from "@/lib/format";

export default function ProductCard({ product, index = 0 }) {
  const { fmt, addToCart, setDrawerOpen, wishlist, toggleWishlist } = useStore();
  const [sizesOpen, setSizesOpen] = useState(false);
  const wished = wishlist.includes(product.slug);
  const totalStock = (product.sizes || []).reduce((s, v) => s + (v.stock || 0), 0);
  const lowStock = totalStock > 0 && totalStock <= 4;

  const badge = (product.tags || []).includes("New")
    ? "New"
    : (product.tags || []).includes("Bestseller")
      ? "Bestseller"
      : lowStock
        ? "Only a few left"
        : null;

  const add = (size) => {
    addToCart(product, size);
    setSizesOpen(false);
    toast.success(`${product.name} · Size ${size} added to bag`);
    setDrawerOpen(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.07, ease: [0.16, 1, 0.3, 1] }}
      className="group"
      data-testid={`product-card-${product.slug}`}
    >
      <div className="relative overflow-hidden">
        <Link
          to={`/product/${product.slug}`}
          data-testid={`product-card-link-${product.slug}`}
          className="relative block aspect-[3/4] overflow-hidden bg-[#EFEAE3]"
        >
          <img
            src={img(product.images?.[0], 900)}
            alt={product.name}
            loading={index < 4 ? "eager" : "lazy"}
            className="absolute inset-0 h-full w-full object-cover transition-all duration-700 ease-out group-hover:scale-[1.05] group-hover:opacity-0"
          />
          {product.images?.[1] && (
            <img
              src={img(product.images[1], 900)}
              alt={`${product.name} alternate`}
              loading="lazy"
              className="absolute inset-0 h-full w-full scale-105 object-cover opacity-0 transition-all duration-700 ease-out group-hover:scale-100 group-hover:opacity-100"
            />
          )}
          {badge && (
            <span className="absolute left-3 top-3 bg-crimson px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
              {badge}
            </span>
          )}
        </Link>
        <button
          data-testid={`product-card-wishlist-${product.slug}`}
          onClick={() => toggleWishlist(product.slug)}
          aria-label="Wishlist"
          className={`absolute right-3 top-3 rounded-full bg-white/90 p-2 backdrop-blur transition-all duration-200 hover:scale-110 ${wished ? "text-crimson" : "text-ink"}`}
        >
          <Heart size={16} fill={wished ? "currentColor" : "none"} />
        </button>
        <div className="absolute inset-x-0 bottom-0 translate-y-full p-2 transition-transform duration-300 ease-out group-hover:translate-y-0">
          {sizesOpen ? (
            <div data-testid={`product-card-sizes-${product.slug}`} className="flex items-center justify-center gap-1 bg-white/95 p-2 backdrop-blur">
              {(product.sizes || []).map((v) => (
                <button
                  key={v.size}
                  data-testid={`product-card-size-${product.slug}-${v.size}`}
                  disabled={!v.stock}
                  onClick={() => add(v.size)}
                  className={`flex-1 px-1 py-2 font-mono text-[11px] transition-colors duration-150 ${v.stock ? "hover:bg-crimson hover:text-white" : "cursor-not-allowed text-linec line-through"}`}
                >
                  {v.size}
                </button>
              ))}
            </div>
          ) : (
            <button
              data-testid={`product-card-quickadd-${product.slug}`}
              onClick={() => setSizesOpen(true)}
              className="flex w-full items-center justify-center gap-1.5 bg-ink/90 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-white backdrop-blur transition-colors duration-200 hover:bg-crimson"
            >
              <Plus size={14} /> Quick Add
            </button>
          )}
        </div>
      </div>

      <div className="pt-4">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-serif text-lg leading-tight sm:text-xl">
            <Link to={`/product/${product.slug}`} className="transition-colors duration-200 hover:text-crimson">
              {product.name}
            </Link>
          </h3>
          <div className="text-right">
            <span data-testid={`product-card-price-${product.slug}`} className="text-sm font-semibold">{fmt(product.price_inr, product.price_usd)}</span>
            {product.mrp_inr > product.price_inr && (
              <span className="ml-2 text-xs text-mutedc line-through">{fmt(product.mrp_inr)}</span>
            )}
          </div>
        </div>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-mutedc">
          {product.category} · {product.fabric}
        </p>
      </div>
    </motion.div>
  );
}
